/**
 * GroupScanner — "Polecane grupy dla Twojej oferty"
 * Shows scored group recommendations in buckets.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target, MapPin, Briefcase, Globe, Users, ChevronRight,
  Loader2, Plus, Check, AlertTriangle, Sparkles, TrendingUp, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';

const CATEGORY_META = {
  breed: { icon: Star, label: 'Tematyczna', color: '#EC4899' },
  sale: { icon: TrendingUp, label: 'Ogłoszeniowa', color: '#F47A3B' },
  regional: { icon: MapPin, label: 'Lokalna', color: '#10B981' },
  community: { icon: Users, label: 'Hodowlana', color: '#3B82F6' },
  broad: { icon: Globe, label: 'Popularna', color: '#8B5CF6' },
  service: { icon: Briefcase, label: 'Usługowa', color: '#F59E0B' },
};

const BUCKET_META = {
  top: { label: 'Najlepsze dla Ciebie', icon: Target, desc: 'Najwyższy potencjał na klientów' },
  local: { label: 'Lokalne', icon: MapPin, desc: 'Grupy z Twojego regionu' },
  niche: { label: 'Branżowe', icon: Star, desc: 'Dopasowane do Twojej specjalizacji' },
  broad: { label: 'Szeroki zasięg', icon: Globe, desc: 'Duże grupy z potencjalnym zasięgiem' },
};

const fmtMembers = (n) => {
  if (!n) return null;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return `${n}`;
};

function ScoreRing({ score }) {
  const color = score >= 70 ? '#10B981' : score >= 50 ? '#F47A3B' : '#94A3B8';
  const circumference = 2 * Math.PI * 18;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-12 h-12 flex-shrink-0" data-testid="score-ring">
      <svg className="w-12 h-12 -rotate-90" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="none" stroke="#f4f4f5" strokeWidth="3" />
        <circle cx="20" cy="20" r="18" fill="none" stroke={color} strokeWidth="3"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold" style={{ color }}>
        {score}
      </span>
    </div>
  );
}

function GroupCard({ group, onAdd, adding }) {
  const meta = CATEGORY_META[group.category] || CATEGORY_META.broad;
  const Icon = meta.icon;
  const isAdding = adding === group.url;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-zinc-100 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-shadow"
      data-testid={`scanner-group-card-${group.url.split('/').pop()}`}
    >
      <div className="flex items-start gap-3">
        <ScoreRing score={group.score} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-bold text-zinc-900 truncate">{group.name}</h3>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${meta.color}15`, color: meta.color }}>
              <Icon className="w-3 h-3" />
              {meta.label}
            </span>
            {group.region !== 'polska' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-600">
                <MapPin className="w-3 h-3" />
                {group.region.charAt(0).toUpperCase() + group.region.slice(1)}
              </span>
            )}
            <span className="text-[10px] text-zinc-400">{fmtMembers(group.members)} czl.</span>
          </div>
          {/* Reasons */}
          <div className="space-y-0.5 mb-3">
            {group.reasons.map((r, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-1 h-1 rounded-full bg-[#F47A3B] flex-shrink-0" />
                <span className="text-[11px] text-zinc-500">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          onClick={() => onAdd(group)}
          disabled={isAdding}
          className="flex-1 h-9 rounded-xl bg-[#F47A3B] hover:bg-[#E06A2E] text-white text-xs font-semibold"
          data-testid={`add-group-${group.url.split('/').pop()}`}
        >
          {isAdding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
          {isAdding ? 'Dodaję...' : 'Dodaj do promocji'}
        </Button>
        <a href={group.url} target="_blank" rel="noopener noreferrer"
          className="h-9 px-3 rounded-xl border border-zinc-200 flex items-center justify-center text-zinc-500 hover:border-zinc-300 hover:text-zinc-700 transition-colors"
          data-testid={`view-group-${group.url.split('/').pop()}`}
        >
          <Globe className="w-3.5 h-3.5" />
        </a>
      </div>
    </motion.div>
  );
}

function AddedConfirmation({ group, onPreparePost }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
      data-testid="added-confirmation"
    >
      <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm mx-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Check className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-bold text-zinc-900">Dodano do Twoich grup</p>
            <p className="text-xs text-zinc-500">{group.name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => onPreparePost('dismiss')}
            variant="outline"
            className="flex-1 h-10 rounded-xl text-sm"
            data-testid="dismiss-added"
          >
            Zamknij
          </Button>
          <Button
            onClick={() => onPreparePost('post')}
            className="flex-1 h-10 rounded-xl bg-[#F47A3B] hover:bg-[#E06A2E] text-white text-sm"
            data-testid="prepare-post-cta"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Przygotuj post
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function GroupScanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const [justAdded, setJustAdded] = useState(null);
  const [addedGroups, setAddedGroups] = useState(new Set());

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchScanner = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups/scanner`, { headers });
      setData(res.data);
    } catch (e) {
      console.error('Scanner fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchScanner(); }, [fetchScanner]);

  const handleAdd = async (group) => {
    setAdding(group.url);
    try {
      await axios.post(`${API}/groups/save`, { name: group.name, url: group.url }, { headers });
      setAddedGroups(prev => new Set([...prev, group.url]));
      setJustAdded(group);
      toast.success(`${group.name} dodano do Twoich grup`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Nie udało się dodać grupy';
      toast.error(msg);
    } finally {
      setAdding(null);
    }
  };

  const handlePostAction = (action) => {
    setJustAdded(null);
    if (action === 'post') {
      navigate('/panel/grupy');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin mx-auto mb-3" />
          <p className="text-sm text-zinc-500">Analizuję najlepsze grupy dla Twojej oferty...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <AlertTriangle className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
        <p className="text-sm text-zinc-500">Nie udało się załadować rekomendacji</p>
      </div>
    );
  }

  const { buckets, profile_complete } = data;

  return (
    <div data-testid="group-scanner">
      {/* Profile incomplete warning */}
      {!profile_complete && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-5">
          <div
            onClick={() => navigate('/panel/profil')}
            className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors"
            data-testid="scanner-profile-warning"
          >
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Uzupelnij profil, aby dostac lepiej dopasowane grupy</p>
              <p className="text-xs text-amber-600">Dodaj lokalizacje, rase i opis oferty</p>
            </div>
            <ChevronRight className="w-4 h-4 text-amber-400" />
          </div>
        </motion.div>
      )}

      {/* Buckets */}
      {Object.entries(BUCKET_META).map(([key, meta]) => {
        const groups = (buckets[key] || []).filter(g => !addedGroups.has(g.url));
        if (groups.length === 0) return null;
        const BucketIcon = meta.icon;
        return (
          <div key={key} className="mb-8" data-testid={`bucket-${key}`}>
            <div className="flex items-center gap-2 mb-3">
              <BucketIcon className="w-4 h-4 text-[#F47A3B]" />
              <h2 className="text-[15px] font-bold text-zinc-900">{meta.label}</h2>
              <span className="text-xs text-zinc-400 ml-1">{meta.desc}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {groups.map((g, i) => (
                <GroupCard
                  key={g.url}
                  group={g}
                  onAdd={handleAdd}
                  adding={adding}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Added confirmation modal */}
      <AnimatePresence>
        {justAdded && (
          <AddedConfirmation group={justAdded} onPreparePost={handlePostAction} />
        )}
      </AnimatePresence>
    </div>
  );
}
